/**
 * Выпадающий список в нашем дизайне (план 117, П3).
 *
 * ЗАЧЕМ. Фильтры разделов использовали нативный `<select>`, и браузер рисовал
 * его своей выпадашкой: серое системное меню macOS посреди нашего интерфейса
 * (жалоба владельца со скриншотом расписания). Плюс нативный триггер
 * растягивался на ширину контейнера, оставляя пустоту справа от короткого
 * значения — второй пункт той же жалобы: «строка должна занимать ровно длину
 * названия».
 *
 * ШИРИНА ПО СОДЕРЖИМОМУ. Триггер — `inline-flex` без `w-full`: он ровно по
 * выбранному значению. Попап шириной не меньше триггера (`min-w-full`) и по
 * самой длинной опции — иначе длинные названия направлений обрезались бы.
 * В формах (фаза 2) триггеру передадут `className="w-full"`: там колонки
 * выравниваются по сетке, и «по содержимому» ломало бы вёрстку.
 *
 * КЛАВИАТУРА обязательна: фильтрами пользуются с клавиатуры так же часто, как
 * мышью, а нативный select это умел. ↑/↓ ведут по списку, Enter выбирает, Esc
 * закрывает и возвращает фокус на триггер.
 *
 * ПОЗИЦИОНИРОВАНИЕ. `absolute` от триггера — как у `BranchMultiSelect`, на
 * котором приём уже обкатан в шапке. Для мест внутри скролл-контейнеров (шапка
 * дровера) попап подрезался бы его границей: там передаётся `fixedPopup`, и
 * координаты считаются от `getBoundingClientRect`.
 *
 * ПОЧЕМУ ПОРТАЛ, А НЕ ПРОСТО `fixed`. `position: fixed` отсчитывается от окна
 * ТОЛЬКО пока ни у одного предка нет `transform`/`filter`/`perspective`. У
 * карточки клиента предок анимируется трансформом, и список уезжал далеко
 * вправо от своей кнопки (жалоба владельца 29.08 со статусом в шапке). Портал
 * в `body` выносит попап из-под трансформированного предка — координаты снова
 * оконные.
 *
 * У НИЖНЕГО КРАЯ fixed-попап раскрывается вверх. Окно «Новый клиент» на
 * телефоне занимает весь экран, и список «Ответственный» у его низа уходил за
 * край — прокрутить fixed-элемент нельзя, пункты были просто недостижимы.
 *
 * ПОИСК (`searchable`, по умолчанию выключен). Педагогов в школе десятки, и
 * листать их стрелкой владелец устал (правка 29.09). Поле ввода — вверху
 * открытого списка, фильтр — по началу слова (`matchesNameQuery`), как во всех
 * поисках людей. Стрелки, Enter и Esc работают по ОТФИЛЬТРОВАННОМУ списку.
 */
import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { matchesNameQuery } from '@/lib/name-search';
import { cn } from '@/lib/utils';

export interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: readonly DropdownOption[];
  /** Что показать, когда значение пустое и такой опции в списке нет. */
  placeholder?: string;
  /** Доступное имя: у фильтров подпись стоит отдельным `<label>`. */
  ariaLabel?: string;
  /** Классы триггера. `w-full` — для полей форм, где важна сетка. */
  className?: string;
  disabled?: boolean;
  /** Попап на fixed-координатах — для скролл-контейнеров, где absolute режется. */
  fixedPopup?: boolean;
  /**
   * Забрать фокус при появлении — для контролов, которые открываются по
   * действию человека (инлайн-правка поля карточки). У нативного `<select>`
   * это делал сам вызывающий через ref; без фокуса клавиатура после клика по
   * карандашу не работает вовсе, а Esc некому поймать.
   */
  autoFocus?: boolean;
  /**
   * Короткая подпись ТРИГГЕРА, когда полная подпись опции нужна только в
   * списке. Так у ведомости: в списке групп важны и педагог, и зал, и отметка
   * о печати — строка на сто с лишним символов. В свёрнутом виде она растягивала
   * шапку и выталкивала за край остальные кнопки (жалоба владельца 01.09).
   */
  triggerLabel?: string;
  /**
   * Значок перед подписью триггера — когда рядом стоят два списка с похожими
   * значениями и нужно видеть, какой из них что делает (план 152 C5: канал
   * ответа и фильтр ленты — оба «Telegram»).
   */
  triggerIcon?: ReactNode;
  /**
   * `id` триггера: в формах подпись связана с полем через `<label htmlFor>`.
   * Без него метка перестаёт указывать на контрол — и для человека со
   * скринридером, и для теста, который ищет поле по подписи.
   */
  id?: string;
  /**
   * Поле поиска вверху открытого списка. Фильтр — по началу слова подписи
   * (правило поиска людей), поэтому режим годится для списков имён.
   */
  searchable?: boolean;
}

/** Потолок высоты списка — тот же, что `max-h-80` у попапа. */
const LIST_MAX_HEIGHT = 320;
/** Зазор между триггером и попапом и отступ попапа от края окна. */
const GAP = 4;
const EDGE = 8;
/**
 * Минимальная ширина попапа с поиском. Триггер бывает узким (педагог по дню
 * недели в серии — ~130px), и попап его ширины резал поле поиска и переносил
 * «Ничего не найдено» (ревью 29.09).
 */
const SEARCH_MIN_WIDTH = 224;

type PopupCoords = {
  left: number;
  width: number;
  /** Окно без полосы прокрутки минус отступы с двух сторон. */
  maxWidth: number;
  maxHeight: number;
} & ({ top: number } | { bottom: number });

export function DropdownSelect({
  value,
  onChange,
  options,
  placeholder = 'Не выбрано',
  ariaLabel,
  className,
  disabled,
  fixedPopup,
  autoFocus,
  triggerLabel,
  triggerIcon,
  id,
  searchable = false,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  // Подсветка клавиатуры отдельна от выбранного значения: пока список открыт,
  // человек ходит по нему, ничего не меняя, — как в нативном select.
  const [active, setActive] = useState(-1);
  const boxRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [coords, setCoords] = useState<PopupCoords | null>(null);
  const [query, setQuery] = useState('');
  const listId = useId();
  // Поиску нужен портал: в модалке с прокруткой absolute-попап срезался её
  // краем, и найденные пункты оказывались за ним.
  const fixed = Boolean(fixedPopup) || searchable;
  // Куда раскрылся список — решается один раз на открытие. Иначе после
  // фильтрации (список короче) прокрутка окна пересчитала бы сторону, и
  // попап перепрыгнул бы через кнопку вместе с полем поиска.
  const directionRef = useRef<'up' | 'down' | null>(null);

  const selected = options.find((o) => o.value === value);
  // Всё, что ниже работает с индексами (подсветка, стрелки, Enter), считает
  // по ВИДИМОМУ списку: без поиска он совпадает с полным.
  const visible =
    searchable && query.trim() ? options.filter((o) => matchesNameQuery(o.label, query)) : options;

  useEffect(() => {
    if (autoFocus) triggerRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e: MouseEvent): void {
      if (
        !boxRef.current?.contains(e.target as Node) &&
        !listRef.current?.contains(e.target as Node)
      )
        setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Fixed-попап пересчитывается при прокрутке и ресайзе: иначе он «отклеится»
  // от триггера, стоит человеку прокрутить страницу под открытым списком.
  useLayoutEffect(() => {
    if (!open || !fixed) return undefined;
    directionRef.current = null;
    function place(): void {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom - GAP - EDGE;
      const above = r.top - GAP - EDGE;
      const el = listRef.current;
      if (directionRef.current === null) {
        // Полная высота списка: содержимое (scrollHeight) плюс рамка —
        // max-height считается по border-box, и без рамки впритык список
        // получал бы лишнюю прокрутку в пару пикселей.
        const natural = el
          ? el.scrollHeight + (el.offsetHeight - el.clientHeight)
          : LIST_MAX_HEIGHT;
        const need = Math.min(natural, LIST_MAX_HEIGHT);
        // Вверх — только если снизу не помещается И сверху места больше:
        // иначе короткий список у середины экрана прыгал бы над кнопкой.
        directionRef.current = below < need && above > below ? 'up' : 'down';
      }
      const minWidth = Math.max(r.width, searchable ? SEARCH_MIN_WIDTH : 0);
      // Собственная ширина попапа — замер от левого края окна. У fixed-попапа
      // доступная ширина = окно минус left, и замер на прежнем left (открыли у
      // правого края, окно сузили, открыли снова) ужимал бы попап навсегда
      // (ревью плана 155, L2).
      let natural = 0;
      if (el) {
        const prevLeft = el.style.left;
        el.style.left = '0px';
        natural = el.offsetWidth;
        el.style.left = prevLeft;
      }
      // По горизонтали — не за край окна: узкий триггер у правого края
      // телефона иначе выталкивал бы широкий попап наружу.
      const width = Math.max(natural, minWidth);
      // Ширина окна без полосы прокрутки: `innerWidth` и `100vw` её включают, и
      // на узком окне с обычной полосой попап залезал под неё краем (план 155
      // Ф2, ревью L4 — список смен с «· с расхождением» на 390 px).
      const viewport = document.documentElement.clientWidth || window.innerWidth;
      const maxWidth = viewport - 2 * EDGE;
      const left = Math.max(EDGE, Math.min(r.left, viewport - EDGE - Math.min(width, maxWidth)));
      if (directionRef.current === 'up') {
        setCoords({
          bottom: window.innerHeight - r.top + GAP,
          left,
          width: minWidth,
          maxWidth,
          maxHeight: Math.min(above, LIST_MAX_HEIGHT),
        });
      } else {
        setCoords({
          top: r.bottom + GAP,
          left,
          width: minWidth,
          maxWidth,
          maxHeight: Math.min(Math.max(below, 0), LIST_MAX_HEIGHT),
        });
      }
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      // Закрыли — координаты прошлого показа не переносим в следующий.
      setCoords(null);
    };
  }, [open, fixed, searchable]);

  // Поиск: фокус сразу в поле — открыл список и печатаешь фамилию, без
  // лишнего клика. Эффект, а не вызов в openList: поля ещё нет в DOM.
  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
  }, [open, searchable]);

  function openList(): void {
    if (disabled) return;
    setQuery('');
    setActive(options.findIndex((o) => o.value === value));
    setOpen(true);
  }

  function close(focusTrigger = true): void {
    setOpen(false);
    setActive(-1);
    if (focusTrigger) triggerRef.current?.focus();
  }

  /**
   * @param viaKeyboard выбор сделан с клавиатуры — тогда фокус возвращаем на
   * триггер: иначе после Enter человек «теряет» место в интерфейсе.
   *
   * При выборе МЫШЬЮ фокус не трогаем. Программный `focus()` переводит Chrome
   * в клавиатурный режим ввода, и следующий клик мышью по любой кнопке рисует
   * синий контур — ровно та «рамка вокруг элементов», на которую владелец
   * жаловался 14.08 и снова 30.08.
   */
  function pick(option: DropdownOption, viaKeyboard = false): void {
    if (option.disabled) return;
    onChange(option.value);
    close(viaKeyboard);
  }

  /** Следующая НЕ отключённая опция видимого списка в заданном направлении. */
  function step(from: number, dir: 1 | -1): number {
    for (let i = 1; i <= visible.length; i += 1) {
      const next = (from + dir * i + visible.length * i) % visible.length;
      if (!visible[next]?.disabled) return next;
    }
    return from;
  }

  /** Стрелки и Enter по открытому списку; true — клавиша обработана. */
  function navigate(e: React.KeyboardEvent, withSpace: boolean): boolean {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((cur) => step(cur < 0 ? 0 : cur, e.key === 'ArrowDown' ? 1 : -1));
      return true;
    }
    if (e.key === 'Enter' || (withSpace && e.key === ' ')) {
      e.preventDefault();
      const option = visible[active];
      if (option) pick(option, true);
      return true;
    }
    return false;
  }

  function onKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        close();
      }
      return;
    }
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (navigate(e, true)) return;
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      setActive(e.key === 'Home' ? step(-1, 1) : step(0, -1));
    }
  }

  /**
   * Клавиши в поле поиска. Пробел и Home/End остаются за полем — это набор
   * текста и ход курсора, а не выбор пункта.
   */
  function onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      // Esc отменяет поиск, а не всё окно: без этого тот же Escape доходил до
      // стека перекрытий (`useEscapeToClose`) и закрывал модалку с формой.
      e.stopPropagation();
      close();
      return;
    }
    // Tab закрывает список и возвращает фокус на кнопку: поле вместе со
    // списком исчезает из DOM, и естественный переход по Tab шёл бы от
    // удалённого узла — куда попадёт фокус, решал бы браузер.
    if (e.key === 'Tab') {
      e.preventDefault();
      close();
      return;
    }
    navigate(e, false);
  }

  function onSearchChange(next: string): void {
    setQuery(next);
    const filtered = next.trim() ? options.filter((o) => matchesNameQuery(o.label, next)) : options;
    // Подсветка — на первом подходящем: набрал фамилию и сразу Enter.
    setActive(filtered.findIndex((o) => !o.disabled));
  }

  const optionId = (i: number): string => `${listId}-option-${i}`;

  const optionButtons = visible.map((option, i) => (
    <button
      key={option.value}
      // id нужен только поиску: поле ввода указывает на подсвеченный пункт
      // через aria-activedescendant.
      id={searchable ? optionId(i) : undefined}
      type="button"
      role="option"
      // Значение в атрибуте — чтобы тест мог выбрать пункт так же, как
      // выбирал у нативного select: по value, а не по подписи.
      data-value={option.value}
      aria-selected={option.value === value}
      disabled={option.disabled}
      onClick={() => pick(option)}
      onMouseEnter={() => setActive(i)}
      className={cn(
        // whitespace-nowrap: пункт занимает свою длину, а не переносится —
        // попап шире, зато названия читаются целиком. У fixed-попапа ширина
        // ограничена окном (см. popupStyle), и пункт длиннее экрана телефона
        // переносится, а не обрезается краем (план 155, выбор группы).
        'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-1.5 text-left text-sm',
        fixed ? 'whitespace-normal' : 'whitespace-nowrap',
        option.disabled
          ? 'cursor-not-allowed text-muted-text opacity-50'
          : 'text-ink-black hover:bg-whisper-white',
        i === active && !option.disabled && 'bg-whisper-white',
        option.value === value && 'font-medium',
      )}
    >
      {option.label}
      {option.value === value ? <Check className="h-4 w-4 shrink-0 text-blueprint-blue" /> : null}
    </button>
  ));

  const popupStyle: React.CSSProperties | undefined =
    fixed && coords
      ? {
          ...('top' in coords ? { top: coords.top } : { bottom: coords.bottom }),
          left: coords.left,
          minWidth: coords.width,
          maxWidth: coords.maxWidth,
          maxHeight: coords.maxHeight,
        }
      : undefined;
  const popupPosition = fixed ? 'fixed' : 'absolute left-0 top-full mt-1';
  // Раскрылся вверх — поле поиска внизу, у самой кнопки: попап привязан к
  // нижнему краю, и при фильтрации укорачивается сверху, а поле стоит на месте.
  const upward = fixed && coords !== null && 'bottom' in coords;
  // min-w-full — только у absolute: у fixed «100%» — это ширина ОКНА, и замер
  // ширины попапа для прижатия к краю получил бы ширину экрана. Fixed-попапу
  // минимум задаёт style (ширина триггера).
  const popupFrame = cn(
    'z-50 max-h-80 rounded-2xl border border-divider-gray bg-canvas-white p-1 shadow-lg',
    !fixed && 'min-w-full',
  );

  const list = searchable ? (
    // С поиском попап — колонка: поле стоит на месте, прокручиваются пункты.
    <div
      ref={listRef}
      className={cn(popupFrame, upward ? 'flex flex-col-reverse' : 'flex flex-col', popupPosition)}
      style={popupStyle}
    >
      <input
        ref={searchRef}
        type="search"
        value={query}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={onSearchKeyDown}
        placeholder="Поиск по имени"
        aria-label="Поиск в списке"
        aria-controls={listId}
        aria-activedescendant={visible[active] ? optionId(active) : undefined}
        autoComplete="off"
        // size=1: поле не распирает попап своей «родной» шириной в 20
        // символов — ширину задают пункты и триггер, поле тянется за ними.
        size={1}
        className={cn(
          'h-8 w-full min-w-0 shrink-0 rounded-lg border border-divider-gray px-3 text-sm text-ink-black outline-none focus:border-blueprint-blue',
          upward ? 'mt-1' : 'mb-1',
        )}
      />
      <div
        id={listId}
        role="listbox"
        aria-label={ariaLabel}
        className="min-h-0 flex-1 overflow-y-auto"
      >
        {optionButtons}
        {visible.length === 0 ? (
          <p className="whitespace-nowrap px-3 py-1.5 text-sm text-muted-text">Ничего не найдено</p>
        ) : null}
      </div>
    </div>
  ) : (
    <div
      ref={listRef}
      id={listId}
      role="listbox"
      aria-label={ariaLabel}
      className={cn(popupFrame, 'overflow-y-auto', popupPosition)}
      style={popupStyle}
    >
      {optionButtons}
    </div>
  );

  return (
    <div ref={boxRef} className={cn('relative', className?.includes('w-full') && 'w-full')}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => (open ? close(false) : openList())}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        className={cn(
          // inline-flex без w-full — ширина ровно по значению (жалоба владельца
          // о пустотах справа). Формы передают w-full сами.
          'inline-flex h-9 max-w-full items-center justify-between gap-2 rounded-full border border-divider-gray bg-canvas-white px-4 text-sm text-ink-black outline-none focus-visible:ring-2 focus-visible:ring-blueprint-blue/40 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
      >
        {triggerIcon}
        <span className="truncate">{triggerLabel ?? selected?.label ?? placeholder}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-text transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {/* Fixed-попап уходит в портал: под трансформированным предком координаты
          окна иначе считаются от него, и список уезжает в сторону. */}
      {open ? (fixed ? createPortal(list, document.body) : list) : null}
    </div>
  );
}
